import { useEffect, useRef, useState } from "react";
import { Send, Loader2, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { askAssistantStream, getAssistantHistory, clearAssistantHistory } from "@/lib/api";

const SESSION_KEY = "ai_assistant_session";
const SUGGESTIONS = ["Aktif kampanyalar neler?", "En uygun fiyatli scooter hangisi?", "Taksit kosullari nedir?"];

const newSession = () => {
  const id = `s_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  localStorage.setItem(SESSION_KEY, id);
  return id;
};

function Bubble({ m }) {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${m.role}`}>
      <div className={`max-w-[88%] px-3.5 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-wrap break-words ${mine ? "bg-slate-900 text-white rounded-2xl rounded-br-md" : "bg-white text-slate-800 border border-slate-200 rounded-2xl rounded-bl-md"}`}>
        {m.text || <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
      </div>
    </div>
  );
}

export default function AiAssistantChat({ compact = false }) {
  const [session, setSession] = useState(() => localStorage.getItem(SESSION_KEY) || newSession());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { getAssistantHistory(session).then((r) => setMessages(r.data.messages || [])).catch(() => {}); }, [session]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ block: "end" }); }, [messages]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    try {
      await askAssistantStream(session, q, (delta) => setMessages((m) => {
        const next = m.slice();
        next[next.length - 1] = { ...next[next.length - 1], text: next[next.length - 1].text + delta };
        return next;
      }));
    } catch (e) {
      toast.error(e.message || "Yanit alinamadi");
      setMessages((m) => m.slice(0, -2));
    } finally { setBusy(false); inputRef.current?.focus(); }
  };

  const reset = async () => {
    try { await clearAssistantHistory(session); } catch { /* ignore */ }
    setMessages([]);
    setSession(newSession());
  };

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="ai-assistant-chat">
      <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-2.5 bg-slate-50">
        {messages.length === 0 && (
          <div className="pt-6 px-2 text-center" data-testid="ai-assistant-empty">
            <span className="mx-auto w-11 h-11 rounded-2xl bg-slate-900 text-amber-300 flex items-center justify-center"><Sparkles className="w-5 h-5" /></span>
            <p className="font-barlow font-bold text-[16px] text-slate-900 mt-3">Fiyat, taksit ve kampanya sor</p>
            <p className="text-[12px] text-slate-500 mt-1">Sistemdeki guncel verilerden yanit verir. Baska islem yapamaz.</p>
            <div className="flex flex-wrap justify-center gap-1.5 mt-4">
              {SUGGESTIONS.map((s) => <button key={s} onClick={() => send(s)} className="text-[12px] font-semibold px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-700 hover:border-slate-400 active:bg-slate-100" data-testid="ai-suggestion">{s}</button>)}
            </div>
          </div>
        )}
        {messages.map((m, i) => <Bubble key={i} m={m} />)}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className={`flex items-center gap-2 border-t border-slate-200 bg-white px-3 ${compact ? "py-2" : "py-2.5"}`}>
        {messages.length > 0 && <button type="button" onClick={reset} title="Sohbeti temizle" className="w-9 h-9 rounded-full flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 shrink-0" data-testid="ai-assistant-clear"><Trash2 className="w-4 h-4" /></button>}
        <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ornek: XRS 50 fiyati ve 6 taksit?" className="flex-1 h-10 rounded-xl bg-slate-100 px-3 text-[14px] outline-none focus:bg-slate-50 focus:ring-2 focus:ring-slate-900/10" data-testid="ai-assistant-input" />
        <button type="submit" disabled={busy || !input.trim()} className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center disabled:opacity-40 shrink-0" data-testid="ai-assistant-send">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
      </form>
    </div>
  );
}
